import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUpload } from '../lib/uploadContext';

const PersistentUploadIndicator: React.FC = () => {
  const { uploadState, cancelUpload } = useUpload();

  // Don't show if not uploading and no error/success
  if (!uploadState.isUploading && !uploadState.error && !uploadState.success) {
    return null;
  }

  // Calculate estimated time remaining
  const getEstimatedTime = () => {
    if (!uploadState.startTime || uploadState.progress <= 0) return null;
    
    const elapsed = Date.now() - uploadState.startTime;
    const rate = uploadState.progress / elapsed;
    const remaining = (100 - uploadState.progress) / rate;
    
    if (remaining < 60000) { // Less than 1 minute
      return `${Math.round(remaining / 1000)} ثانیه`;
    } else {
      return `${Math.round(remaining / 60000)} دقیقه`;
    }
  };

  const estimatedTime = getEstimatedTime();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-16 inset-x-4 z-50 max-w-md mx-auto"
      >
        <div className={`rounded-lg shadow-lg border p-4 ${
          uploadState.error 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
            : uploadState.success
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            {uploadState.isUploading && (
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-blue-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            )}
            {uploadState.success && (
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {uploadState.error && (
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className={`text-sm font-medium ${
                  uploadState.error 
                    ? 'text-red-900 dark:text-red-200' 
                    : uploadState.success
                    ? 'text-green-900 dark:text-green-200'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {uploadState.error || (uploadState.success ? 'پست با موفقیت ایجاد شد!' : uploadState.message)}
                </p>
                {uploadState.isUploading && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-left">
                    <div>{Math.round(uploadState.progress)}%</div>
                    {estimatedTime && uploadState.progress < 95 && (
                      <div className="text-xs">باقی‌مانده: {estimatedTime}</div>
                    )}
                  </div>
                )}
              </div>
              {uploadState.isUploading && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <motion.div
                    className="bg-blue-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadState.progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              )}
            </div>
            {(uploadState.error || uploadState.success) && (
              <button
                onClick={cancelUpload}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Keep the old component for backward compatibility (used in createPost for local display)
interface UploadProgressProps {
  progress: number;
  message?: string;
  startTime?: number | null;
}

const UploadProgress: React.FC<UploadProgressProps> = ({ progress, message = "در حال آپلود پست...", startTime }) => {
  // Calculate estimated time remaining
  const getEstimatedTime = () => {
    if (!startTime || progress <= 0) return null;
    
    const elapsed = Date.now() - startTime;
    const rate = progress / elapsed;
    const remaining = (100 - progress) / rate;
    
    if (remaining < 60000) { // Less than 1 minute
      return `${Math.round(remaining / 1000)} ثانیه`;
    } else {
      return `${Math.round(remaining / 60000)} دقیقه`;
    }
  };

  const estimatedTime = getEstimatedTime();
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="mt-3 bottom-16 left-1/2 transform -translate-x-1/2 z-50"
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-80">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-blue-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {message}
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400 text-left">
                <div>{Math.round(progress)}%</div>
                {estimatedTime && progress < 95 && (
                  <div className="text-xs">باقی‌مانده: {estimatedTime}</div>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <motion.div
                className="bg-blue-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UploadProgress;
export { PersistentUploadIndicator };